import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { CheckInResult } from '../../components/checkin/CheckInResult';
import { LoadingSpinner } from '../../components/checkin/LoadingSpinner';
import { useAnonymousCheckInMutation } from '../../hooks/useAnonymousCheckInMutation';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const EventCheckIn: React.FC<IProps> = props => {
	const { className, styles = [] } = props;
	const { eventId } = useParams<{ eventId: string }>();
	const location = useLocation();

	// Extract confirmation number from URL parameters
	const urlParams = new URLSearchParams(location.search);
	const confirmationNumber = urlParams.get('confirmation');

	const checkInMutation = useAnonymousCheckInMutation();

	// Trigger check-in on mount
	React.useEffect(() => {
		if (eventId && confirmationNumber) {
			checkInMutation.mutate({ eventId, confirmationNumber });
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Show error for missing parameters
	if (!eventId || !confirmationNumber) {
		return (
			<div className={`event-checkin-container ${className || ''} ${css(styleSheet.container, ...styles)}`}>
				<div className={css(styleSheet.surveyContent)}>
					<CheckInResult
						type='error'
						title='Invalid Check-In Link'
						message='This check-in link is missing required information. Please contact the event organizer.'
					/>
				</div>
			</div>
		);
	}

	return (
		<div className={`event-checkin-container ${className || ''} ${css(styleSheet.container, ...styles)}`}>
			<div className={css(styleSheet.surveyContent)}>
				{checkInMutation.isPending ? (
					<LoadingSpinner message='Checking you in...' />
				) : checkInMutation.error ? (
					<CheckInResult
						type='error'
						title='Check-In Failed'
						message={
							checkInMutation.error.message || 'Unable to complete check-in. Please try again or contact support.'
						}
					/>
				) : checkInMutation.data?.alreadyCheckedIn ? (
					<CheckInResult
						type='warning'
						title='Already Checked In'
						message='You have already been checked in for this event.'
						subtitle={checkInMutation.data.attendeeName}
					/>
				) : checkInMutation.data?.success ? (
					(() => {
						const registration = checkInMutation.data.registration;
						const guestCount = registration?.attendees ? registration.attendees.filter(a => !a.isPrimary).length : 0;
						const guestText = guestCount > 0 ? (guestCount === 1 ? '+1 Guest' : `+${guestCount} Guests`) : undefined;

						return (
							<CheckInResult
								type='success'
								title='Check In Successful!'
								message='Welcome'
								subtitle={checkInMutation.data.attendeeName}
								guestCount={guestText}
							/>
						);
					})()
				) : null}
			</div>
		</div>
	);
};
